import {
  promoCodesService,
  type CreatePromoCodePayload,
  type GeneratePromoCodesPayload,
  type DomainValue,
  type PromoCodeDto,
  type PromoCodesListParams,
  type UpdatePromoCodePayload,
} from '@/services/promo-codes.service';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

interface PromoCodesState {
  promoCodes: PromoCodeDto[];
  total: number;
  page: number;
  limit: number;
  availableDomains: DomainValue[];
  isLoading: boolean;
  error: string | null;
  setError: (error: string | null) => void;
  fetchPromoCodes: (params?: PromoCodesListParams) => Promise<void>;
  createPromoCode: (payload: CreatePromoCodePayload) => Promise<PromoCodeDto>;
  createPromoCodesBulk: (payload: GeneratePromoCodesPayload) => Promise<PromoCodeDto[]>;
  updatePromoCode: (id: string, payload: UpdatePromoCodePayload) => Promise<PromoCodeDto>;
  setPromoCodeActive: (id: string, active: boolean) => Promise<PromoCodeDto>;
}

export const usePromoCodesStore = create<PromoCodesState>()(
  devtools(
    (set) => ({
      promoCodes: [],
      total: 0,
      page: 1,
      limit: 10,
      availableDomains: [],
      isLoading: false,
      error: null,
      setError: (error) => {
        set({ error }, undefined, 'promoCodes/setError');
      },
      fetchPromoCodes: async (params) => {
        set({ isLoading: true, error: null }, undefined, 'promoCodes/fetchStart');

        try {
          const response = await promoCodesService.list(params);
          set(
            {
              promoCodes: response.items,
              total: response.total,
              page: response.page,
              limit: response.limit,
              availableDomains: response.availableDomains,
            },
            undefined,
            'promoCodes/fetchSuccess'
          );
        } catch (err: unknown) {
          const message = err instanceof Error ? err.message : 'Failed to load promo codes';
          set({ error: message }, undefined, 'promoCodes/fetchError');
        } finally {
          set({ isLoading: false }, undefined, 'promoCodes/fetchEnd');
        }
      },
      createPromoCode: async (payload) => {
        set({ error: null }, undefined, 'promoCodes/createStart');

        try {
          const created = await promoCodesService.createSingle(payload);
          return created;
        } catch (err: unknown) {
          const message = err instanceof Error ? err.message : 'Failed to create promo code';
          set({ error: message }, undefined, 'promoCodes/createError');
          throw err;
        }
      },
      createPromoCodesBulk: async (payload) => {
        set({ error: null }, undefined, 'promoCodes/createBulkStart');

        try {
          const created = await promoCodesService.createBulk(payload);
          return created;
        } catch (err: unknown) {
          const message = err instanceof Error ? err.message : 'Failed to generate promo codes';
          set({ error: message }, undefined, 'promoCodes/createBulkError');
          throw err;
        }
      },
      updatePromoCode: async (id, payload) => {
        set({ error: null }, undefined, 'promoCodes/updateStart');

        try {
          const updated = await promoCodesService.update(id, payload);
          return updated;
        } catch (err: unknown) {
          const message = err instanceof Error ? err.message : 'Failed to update promo code';
          set({ error: message }, undefined, 'promoCodes/updateError');
          throw err;
        }
      },
      setPromoCodeActive: async (id, active) => {
        set({ error: null }, undefined, 'promoCodes/setActiveStart');

        try {
          const updated = await promoCodesService.setActive(id, active);
          return updated;
        } catch (err: unknown) {
          const message = err instanceof Error ? err.message : 'Failed to update promo code status';
          set({ error: message }, undefined, 'promoCodes/setActiveError');
          throw err;
        }
      },
    }),
    { name: 'promo-codes-store' }
  )
);
